import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid'; 
import Box from '@mui/material/Box'; 
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import { FormControlLabel, Checkbox } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import Slider from '@mui/material/Slider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import Title from './Title';

const ChatbotSettings = ({user, accessToken}) => {
  const [botName, setBotName] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState('');
  const [expertise, setExpertise] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [restrictions, setRestrictions] = useState('');
  const [additionalinstructions, setAdditionalInstructions] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [originalFiles, setOriginalFiles] = useState([]);  
  const [selectedLLM, setSelectedLLM] = useState('qwen3');
  const [apiKey, setApiKey] = useState('');
  const [imageGeneration, setImageGeneration] = useState(false);
  const [visibility, setVisibility] = useState('0');
  const [temperature, setTemperature] = useState(0.6);
  const [currentTooltip, setCurrentTooltip] = useState('');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      const fetchChatbot = async () => {
        try {
          const bodyData = {
            access_token: accessToken,
            assistant_key: id
          };
          const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/get_assistant', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyData),
          });

          if (response.ok) {
            const result = await response.json();
            if (result.status === "Success") {
              setBotName(result.name);
              setDescription(result.description);
              setPurpose(result.purpose);
              setExpertise(result.expertise);
              setCapabilities(result.capabilities);
              setRestrictions(result.restrictions);
              setAdditionalInstructions(result.additional);
              setSelectedLLM(result.llm);
              setTemperature(parseFloat(result.temperature));
              setApiKey(result.apikey);
              setImageGeneration(result.imagegeneration === 1);
              setVisibility(result.visibility);
              fetchKnowledgeBaseFiles();
            } else {
              console.error('Error fetching chatbot data:', result.error);
            }
          } else {
            console.error('Error fetching chatbot data:', response.statusText);
          }
        } catch (error) {
          console.error('Error fetching chatbot data:', error);
        }
      };
      fetchChatbot();      
    }
  }, [id]);

  const fetchKnowledgeBaseFiles = async () => {
    try {
      const bodyData = {
        access_token: accessToken,
        assistant_key: id
      };
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/get_knowledge_base_files/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'Success') {
          const existingFiles = result.files.map((fileObj) => ({
            name: fileObj.name,
            isNew: false, 
          }));
          setUploadedFiles(existingFiles);
          setOriginalFiles(existingFiles);
        } else {
          console.error('Error fetching knowledge base files:', result.message);
        }
      } else {
        console.error('Error fetching knowledge base files:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching knowledge base files:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {      
      let url = "";
      let chatbotSettings = {}
      if (id) {       
        url = process.env.REACT_APP_API_BASE_URL + 'actions/update_assistant';
        chatbotSettings = {
          access_token: accessToken,
          assistant_key: id,
          name: botName,
          description: description,
          purpose: purpose,  
          expertise: expertise,
          capabilities: capabilities,
          restrictions: restrictions,
          additional: additionalinstructions,
          llm: selectedLLM,
          api_key: apiKey,
          temperature: parseFloat(temperature), 
          image_generation: imageGeneration,
          visibility: visibility
        };
      }
      else{
        url = process.env.REACT_APP_API_BASE_URL + 'actions/create_assistant';
        chatbotSettings = {
          access_token: accessToken,
          name: botName,
          description: description,
          purpose: purpose,  
          expertise: expertise,
          capabilities: capabilities,
          restrictions: restrictions,
          additional: additionalinstructions,
          llm: selectedLLM,
          api_key: apiKey,
          temperature: parseFloat(temperature), 
          image_generation: imageGeneration,
          visibility: visibility
        };
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(chatbotSettings)
      });
  
      if (response.ok) {
        const result = await response.json();
        if (result.status === "Success"){  
          const assistantKey = id || result.key;
        
          if ((uploadedFiles.length > 0) || (originalFiles.length > 0)){
            const newFiles = uploadedFiles.filter((file) => file.isNew);
            const existingFiles = uploadedFiles.filter((file) => !file.isNew);
            if (newFiles.length > 0) {
              // Upload new files
              const formData = new FormData();
              formData.append('access_token', accessToken);
              formData.append('assistant_key', assistantKey);

              newFiles.forEach((file) => {
                formData.append('files', file.file);
              });
            
              const uploadResponse = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/upload_knowledge_base/', {
                method: 'POST',
                body: formData 
              });
              if (uploadResponse.ok) {
                const uploadResult = await uploadResponse.json();
                if (uploadResult.status === "Success") {
                  setAlert({ open: true, message: 'AI Assistant created and files uploaded successfully!', severity: 'success' });
                } else {
                  console.error('Error uploading files:', uploadResult);
                  setAlert({ open: true, message: 'AI Assistant created but error uploading files!', severity: 'error' });
                }
              } else {
                console.error('Error uploading files:', uploadResponse.statusText);
                setAlert({ open: true, message: 'AI Assistant created but error uploading files!', severity: 'error' });
              }
            }
            // Remove deleted existing files             
            const filesToDelete = originalFiles.filter((file) => !existingFiles.some((f) => f.name === file.name));
            if (filesToDelete.length > 0) {
              for (let file of filesToDelete) {
                try {
                  let deleteBody = { access_token: accessToken, assistant_key: assistantKey, filename: file.name };
                  await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/delete_knowledge_base_file/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(deleteBody)
                  });
                } catch (error) {
                  console.error(`Error deleting file ${file}:`, error);
                }
              }
            }
          } else {
            setAlert({ open: true, message: 'AI Assistant created successfully!', severity: 'success' });
          }
          navigate("/assistant/" + assistantKey);
          
        }
        else{
          console.error('Error creating AI Assistant:', result);
          setAlert({ open: true, message: 'Error creating AI Assistant!', severity: 'error' });
        }
      } else {
        console.error('Error creating AI Assistant:', response.statusText);
        setAlert({ open: true, message: 'Error creating AI Assistant!', severity: 'error' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setAlert({ open: true, message: 'Error submitting form!', severity: 'error' });
    } finally {
      setLoading(false); 
    }
  };

  const tooltips = {
    botName: "Provide a unique and descriptive name for your assistant. This name will be used to identify your assistant in the system.",
    description: "Write a brief summary of what your assistant is designed to do. This should highlight the primary purpose and general capabilities.",
    purpose: "Define your assistant's identity and personality traits. Describe the assistant's intended role and purpose, including how it should engage with users (e.g., friendly, formal, expert advisor).",
    expertise: "Outline the specific areas of knowledge or skills your assistant should focus on. This could range from technical knowledge to student support expertise.",
    capabilities: "List the specific functionalities your assistant should be equipped with. These might include answering FAQs, providing recommendations, offering step-by-step guidance, etc.",
    restrictions: "Specify any limitations or areas where the assistant should not engage. This helps maintain focus and prevents the assistant from providing information outside its intended scope.",
    additionalInstructions: "Provide any other details or nuanced behaviors you would like the assistant to exhibit. This can include special instructions, rules, or guidelines for responding to specific types of inquiries.",
    knowledgeBase: "If applicable, upload documents that contain essential information your assistant should have access to for generating responses.",
    modelSelection: "Choose the language model that best fits your assistant's needs. LLaMA and Qwen models run at BUas servers and are free. The GPT and Claude models require an API-key.",
    temperature: "Adjust the creativity slider to control the randomness of the responses (with lower creativity, the responses are more deterministic).",
    visibility: "Private assistants are only visible to the owner (the assistant will be listed on the \"My AI Assistants\" list). Unlisted assistants are not listed publicly but can be accessed by anyone with the link (useful for testing before making them public). Public assistants are accessible to other users at BUas (the assistant will be listed on the \"Community AI Assistants\" list).",
    imageGeneration: "Allow the assistant to generate images."
  };

  const handleFileUpload = (files) => {
    const fileList = Array.from(files);
    const validFiles = fileList.filter(file => {
      const extension = file.name.split('.').pop();
      return ['pdf', 'docx', 'txt', 'md', 'csv'].includes(extension);
    });
    const newFiles = validFiles.map(file => ({
      name: file.name,
      file,
      isNew: true,
    }));
    setUploadedFiles((prevFileList) => [...prevFileList, ...newFiles]);
  };

  const handleRemoveFile = (indexToRemove) => {
    setUploadedFiles((prevFileList) =>
      prevFileList.filter((_, index) => index !== indexToRemove)
    );
  };
 
  const handleTemperatureChange = (event, newValue) => {
    setTemperature(newValue);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Title>{id ? 'Edit AI Assistant' : 'Create a new AI Assistant'}</Title>
      <Grid container spacing={3}> {/* MUI Grid for layout */}        
        <Grid item xs={12}>
          <Tooltip title={tooltips.botName} open={currentTooltip === 'botName'} arrow>
            <TextField
              label="Assistant Name"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              fullWidth
              required
              onFocus={() => setCurrentTooltip('botName')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip title={tooltips.description} open={currentTooltip === 'description'} arrow>
            <TextField
              label="Short Description of the Assistant"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              required
              onFocus={() => setCurrentTooltip('description')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
              inputProps={{ maxLength: 500 }}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip title={tooltips.purpose} open={currentTooltip === 'purpose'} arrow>
            <TextField
              label="Identity, Personality, and Purpose"
              multiline
              minRows={3}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              fullWidth
              required
              onFocus={() => setCurrentTooltip('purpose')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip title={tooltips.expertise} open={currentTooltip === 'expertise'} arrow>
            <TextField
              label="Expertise"
              multiline
              minRows={3}
              value={expertise}
              onChange={(e) => setExpertise(e.target.value)}
              fullWidth
              onFocus={() => setCurrentTooltip('expertise')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip title={tooltips.capabilities} open={currentTooltip === 'capabilities'} arrow>
            <TextField
              label="Capabilities"
              multiline
              minRows={3}
              value={capabilities}
              onChange={(e) => setCapabilities(e.target.value)}
              fullWidth
              onFocus={() => setCurrentTooltip('capabilities')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip title={tooltips.restrictions} open={currentTooltip === 'restrictions'} arrow>
            <TextField
              label="Restrictions"
              multiline
              minRows={3}
              value={restrictions}
              onChange={(e) => setRestrictions(e.target.value)}
              fullWidth
              onFocus={() => setCurrentTooltip('restrictions')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip title={tooltips.additionalInstructions} open={currentTooltip === 'additionalInstructions'} arrow>
            <TextField
              label="Additional Instructions"
              multiline
              minRows={3}
              value={additionalinstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              fullWidth
              onFocus={() => setCurrentTooltip('additionalInstructions')}
              onBlur={() => setCurrentTooltip('')}
              autoComplete="off"
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            title={tooltips.knowledgeBase}
            open={currentTooltip === 'knowledgeBase'}
            arrow
          >
            <Box
              onMouseEnter={() => setCurrentTooltip('knowledgeBase')} 
              onMouseLeave={() => setCurrentTooltip('')} 
            >
              <Typography variant="body" gutterBottom> 
                Knowledge Base 
              </Typography>              
              <div
                style={{
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '10px 15px',
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px',
                  cursor: 'pointer'
                }}
              >
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.md,.csv"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                  style={{ display: 'none' }}
                  id="upload-button"
                />
                <label htmlFor="upload-button" style={{ display: 'flex', alignItems: 'center' }}>
                  <Button variant="contained" component="span">
                    Add File
                  </Button>
                </label>

                <div style={{ marginLeft: 8}}>
                  {uploadedFiles.length > 0 ? (
                    <ul style={{ padding: 0, listStyle: 'none' }}>
                      {uploadedFiles.map((file, index) => (
                        <li
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '5px 10px',
                            marginBottom: 5,
                          }}
                        >
                          <DescriptionIcon />
                          {file.name}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(index)}
                            sx={{ verticalAlign: 'middle', marginLeft: '8px' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    'No files selected'
                  )}
                </div>
              </div>
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip title={tooltips.modelSelection} open={currentTooltip === 'modelSelection'} arrow>
            <FormControl fullWidth>
              <InputLabel>Large Language Model</InputLabel>
              <Select
                value={selectedLLM}
                label="Large Language Model"
                required
                onChange={(e) => setSelectedLLM(e.target.value)}
                onOpen={() => setCurrentTooltip('modelSelection')}
                onClose={() => setCurrentTooltip('')}
              >
                <MenuItem value="qwen3">Qwen 3.6 (27B)</MenuItem>
                <MenuItem value="llama3.3:70b">Llama 3.3 (70B)</MenuItem>
                <MenuItem value="llama3.1:8b">Llama 3.1 (8B)</MenuItem>
                <MenuItem value="claude-sonnet-4-0">Claude Sonnet 4.6</MenuItem>
                <MenuItem value="claude-opus-4-1">Claude Opus 4.7</MenuItem>
                <MenuItem value="gpt-4o">GPT-4o</MenuItem>
                <MenuItem value="gpt-4">GPT-4</MenuItem>
                <MenuItem value="gpt-4-turbo">GPT-4-Turbo</MenuItem>
              </Select>
            </FormControl>
          </Tooltip>
        </Grid>  
        <Grid item xs={12} sm={6}>
          <Tooltip title={tooltips.temperature} open={currentTooltip === 'temperature'} arrow>
            <FormControl fullWidth>
              <Typography>Creativity Level</Typography>
              <Slider
                value={temperature}
                min={0}
                max={1}
                step={0.01}
                onChange={handleTemperatureChange}
                valueLabelDisplay="auto"
                onFocus={() => setCurrentTooltip('temperature')}
                onBlur={() => setCurrentTooltip('')}
              />
            </FormControl>
          </Tooltip>
        </Grid>
        {(selectedLLM.startsWith('gpt') || selectedLLM.startsWith('claude')) && (
          <Grid item xs={12}>
            <TextField
              label="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              fullWidth
              required
              autoComplete="off"
            />
          </Grid>
        )}      
        <Grid item xs={12} sm={6}>
        <Tooltip title={tooltips.visibility} open={currentTooltip === 'visibility'} arrow>
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibility}
                label="Visibility"
                required
                onChange={(e) => setVisibility(e.target.value)}
                onOpen={() => setCurrentTooltip('visibility')}
                onClose={() => setCurrentTooltip('')}
              >
                <MenuItem value="0">Private</MenuItem>
                <MenuItem value="3">Unlisted</MenuItem>
                <MenuItem value="1">Public</MenuItem>
              </Select>
            </FormControl>
          </Tooltip>               
        </Grid>

        <Grid item xs={12} sm={6}>
          <Tooltip title={tooltips.imageGeneration} open={currentTooltip === 'imageGeneration'} arrow>
            <FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={imageGeneration}
                    onChange={(e) => setImageGeneration(e.target.checked)}
                    onFocus={() => setCurrentTooltip('imageGeneration')}
                    onBlur={() => setCurrentTooltip('')}
                  />
                }
                label="Enable Image Generation"
              />
            </FormControl>
          </Tooltip>
        </Grid>
        
        
        <Grid item xs={12}>
          <Button variant="contained" color="primary" type="submit" sx={{ width: '100%' }} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (id ? 'Update AI Assistant' : 'Create AI Assistant')}
          </Button>
        </Grid>
      </Grid>
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </form>
  );
};

export default ChatbotSettings;
